import React from 'react';
import { ClientActivityPoint } from '../interfaces/Frameworks';
import { Theme } from '../Theme';

type EngagementRange = 'last_week' | 'last_month' | 'last_year' | 'all_time';

type Props = {
    activityPoints: Array<ClientActivityPoint>;
    allTimeStartDate?: Date | null;
    title?: string;
    totalUnitLabel?: string;
};

type State = {
    range: EngagementRange;
};

const RANGE_OPTIONS: Array<{ value: EngagementRange; label: string }> = [
    { value: 'last_week', label: 'Last week' },
    { value: 'last_month', label: 'Last month' },
    { value: 'last_year', label: 'Last year' },
    { value: 'all_time', label: 'All time' },
];

function formatDateLabel(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString(undefined, { month: 'short' }).toUpperCase();
    return `${day} ${month}`;
}

function parseDateKey(dateKey: string): Date {
    const [year, month, day] = dateKey.split('-').map(Number);
    return new Date(year || 0, (month || 1) - 1, day || 1);
}

function formatDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function startOfDay(date: Date): Date {
    const nextDate = new Date(date);
    nextDate.setHours(0, 0, 0, 0);
    return nextDate;
}

export class EngagementChart extends React.Component<Props, State> {
    state: State = {
        range: 'last_month',
    };

    private handleRangeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ range: event.target.value as EngagementRange });
    };

    private buildSeries() {
        const sortedPoints = [...this.props.activityPoints]
            .sort((left, right) => left.date.localeCompare(right.date));
        const countByDate = new Map<string, number>();

        sortedPoints.forEach((point) => {
            countByDate.set(point.date, point.count);
        });

        const today = startOfDay(new Date());
        const startDate = (() => {
            switch (this.state.range) {
                case 'last_week': {
                    const nextStartDate = new Date(today);
                    nextStartDate.setDate(today.getDate() - 6);
                    return nextStartDate;
                }
                case 'last_month': {
                    const nextStartDate = new Date(today);
                    nextStartDate.setDate(today.getDate() - 29);
                    return nextStartDate;
                }
                case 'last_year': {
                    const nextStartDate = new Date(today);
                    nextStartDate.setDate(today.getDate() - 364);
                    return nextStartDate;
                }
                case 'all_time':
                default: {
                    const { allTimeStartDate } = this.props;
                    if (allTimeStartDate && !isNaN(allTimeStartDate.getTime())) {
                        const signUpDate = startOfDay(allTimeStartDate);
                        return signUpDate <= today ? signUpDate : today;
                    }

                    if (sortedPoints.length > 0) {
                        return startOfDay(parseDateKey(sortedPoints[0].date));
                    }

                    const nextStartDate = new Date(today);
                    nextStartDate.setDate(today.getDate() - 29);
                    return nextStartDate;
                }
            }
        })();

        const dates: Array<Date> = [];
        const values: Array<number> = [];

        for (let cursor = new Date(startDate); cursor <= today; cursor.setDate(cursor.getDate() + 1)) {
            const key = formatDateKey(cursor);
            dates.push(new Date(cursor));
            values.push(countByDate.get(key) || 0);
        }

        return {
            dates,
            values,
            total: values.reduce((sum, value) => sum + value, 0),
        };
    }

    public render(): React.ReactNode {
        const { title = 'Topics completed', totalUnitLabel } = this.props;
        const { range } = this.state;
        const { dates, values, total } = this.buildSeries();
        const safeValues = values.length > 0 ? values : Array(7).fill(0);
        const maxValue = Math.max(...safeValues, 0);
        const chartMax = Math.max(20, Math.ceil(maxValue / 5) * 5 || 5);
        const yTicks = [chartMax, chartMax * 0.75, chartMax * 0.5, chartMax * 0.25, 0];

        const width = 720;
        const height = 214;
        const padding = { top: 14, right: 18, bottom: 34, left: 54 };
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;
        const step = safeValues.length > 1 ? chartWidth / (safeValues.length - 1) : 0;

        const points = safeValues.map((value, index) => {
            const x = padding.left + (step * index);
            const y = padding.top + chartHeight - ((value / chartMax) * chartHeight);
            return `${x},${y}`;
        }).join(' ');

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const xTickIndexes = Array.from(new Set([
            0,
            Math.floor((safeValues.length - 1) / 3),
            Math.floor(((safeValues.length - 1) * 2) / 3),
            safeValues.length - 1,
        ]));

        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <div style={styles.title}>{title}</div>
                        <div style={styles.subtitle}>
                            {total} total{totalUnitLabel ? ` ${totalUnitLabel}` : ''}
                        </div>
                    </div>
                    <div style={styles.rangeControl}>
                        <select value={range} onChange={this.handleRangeChange} style={styles.rangeSelect}>
                            {RANGE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <span style={styles.rangeChevron} aria-hidden='true'>
                            <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                                <polyline
                                    points='6 9 12 15 18 9'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </span>
                    </div>
                </div>

                <svg viewBox={`0 0 ${width} ${height}`} style={styles.chart} role='img' aria-label='Engagement activity chart'>
                    {yTicks.map((tickValue) => {
                        const y = padding.top + chartHeight - ((tickValue / chartMax) * chartHeight);

                        return (
                            <g key={tickValue}>
                                <text
                                    x={padding.left - 12}
                                    y={y + 7}
                                    textAnchor='end'
                                    style={styles.yAxisLabel}
                                >
                                    {tickValue}
                                </text>
                                <line
                                    x1={padding.left}
                                    y1={y}
                                    x2={width - padding.right}
                                    y2={y}
                                    style={styles.gridLine}
                                />
                            </g>
                        );
                    })}

                    {xTickIndexes.map((tickIndex) => {
                        const x = padding.left + (step * tickIndex);
                        const tickDate = dates[tickIndex] || today;

                        return (
                            <text
                                key={tickIndex}
                                x={x}
                                y={height - 10}
                                textAnchor={tickIndex === 0 ? 'start' : tickIndex === safeValues.length - 1 ? 'end' : 'middle'}
                                style={styles.xAxisLabel}
                            >
                                {formatDateLabel(tickDate)}
                            </text>
                        );
                    })}

                    <polyline
                        points={points}
                        fill='none'
                        stroke='#7B7AE6'
                        strokeWidth='3'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                </svg>
            </div>
        );
    }
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        flexWrap: 'wrap',
    },
    title: {
        fontSize: '14px',
        fontWeight: 500,
        color: Theme.Colours.text,
        lineHeight: 1.2,
        marginLeft: '12px',
    },
    subtitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: Theme.Colours.text,
        marginTop: '6px',
        marginLeft: '12px',
        lineHeight: 1.1,
    },
    rangeControl: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
    },
    rangeSelect: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '12px 44px 12px 16px',
        borderRadius: '14px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
        color: 'rgba(0, 0, 0, 0.62)',
        cursor: 'pointer',
        boxShadow: 'none',
        minWidth: '148px',
    },
    rangeChevron: {
        position: 'absolute',
        right: '14px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(0, 0, 0, 0.62)',
        pointerEvents: 'none',
    },
    chart: {
        maxWidth: '100%',
        height: '300px',
        overflow: 'visible',
        display: 'block',
    },
    gridLine: {
        stroke: 'rgba(0, 0, 0, 0.12)',
        strokeWidth: 1,
        strokeDasharray: '6 8',
    },
    yAxisLabel: {
        fontSize: '11px',
        fill: 'rgba(0, 0, 0, 0.4)',
    },
    xAxisLabel: {
        fontSize: '11px',
        fill: 'rgba(0, 0, 0, 0.4)',
    },
};
